import { useMemo } from 'react';
import { 
  formatDate as utilFormatDate,
  formatDateTime as utilFormatDateTime,
  formatDateOnly as utilFormatDateOnly,
  formatRelativeTime as utilFormatRelativeTime,
  formatDateForInput as utilFormatDateForInput,
  parseInputToUTC as utilParseInputToUTC,
  parseDateToUTC as utilParseDateToUTC,
  isToday as utilIsToday,
  isThisWeek as utilIsThisWeek,
  isThisMonth as utilIsThisMonth,
  getTimezoneOffset as utilGetTimezoneOffset,
  getBrowserTimezone
} from '../lib/dateUtils';
import type { DateFormatOptions } from '../lib/dateUtils';

export const useDateFormat = () => {
  const browserTimezone = useMemo(() => {
    return getBrowserTimezone();
  }, []);

  const formatDate = (
    dateInput: string | Date | null | undefined,
    options: Omit<DateFormatOptions, 'timezone'> = {}
  ): string => {
    return utilFormatDate(dateInput, { ...options, timezone: browserTimezone });
  };

  const formatDateTime = (
    dateInput: string | Date | null | undefined
  ): string => {
    return utilFormatDateTime(dateInput, browserTimezone);
  };

  const formatDateOnly = (
    dateInput: string | Date | null | undefined
  ): string => {
    return utilFormatDateOnly(dateInput, browserTimezone);
  };

  const formatRelativeTime = (
    dateInput: string | Date | null | undefined
  ): string => {
    return utilFormatRelativeTime(dateInput);
  };

  const formatDateForInput = (
    dateInput: string | Date | null | undefined
  ): string => {
    return utilFormatDateForInput(dateInput, browserTimezone);
  };

  const parseInputToUTC = (
    dateInput: string
  ): string | null => {
    return utilParseInputToUTC(dateInput);
  };

  const parseDateToUTC = (
    dateInput: string,
    startOfDay: boolean = true
  ): string | null => {
    return utilParseDateToUTC(dateInput, startOfDay);
  };

  const isToday = (
    dateInput: string | Date | null | undefined
  ): boolean => {
    return utilIsToday(dateInput, browserTimezone);
  };

  const isThisWeek = (
    dateInput: string | Date | null | undefined
  ): boolean => {
    return utilIsThisWeek(dateInput, browserTimezone);
  };

  const isThisMonth = (
    dateInput: string | Date | null | undefined
  ): boolean => {
    return utilIsThisMonth(dateInput, browserTimezone);
  };

  const getTimezoneOffset = (): string => {
    return utilGetTimezoneOffset(browserTimezone);
  };

  return {
    formatDate,
    formatDateTime,
    formatDateOnly,
    formatRelativeTime,
    formatDateForInput,
    parseInputToUTC,
    parseDateToUTC,
    isToday,
    isThisWeek,
    isThisMonth,
    getTimezoneOffset,
    browserTimezone,
  };
};

// Hook for getting just the browser timezone without formatting functions
export const useBrowserTimezone = (): string => {
  return useMemo(() => {
    return getBrowserTimezone();
  }, []);
};

